// Telefonie-Qualität (Z-09): Übersicht über alle Anrufe der CRM-Telefonie: Alarme der letzten 24 Stunden, Kennzahlen, Verlauf je Tag,
// je Mitarbeiter, je Anruf, Störungsprotokoll und Gerätetests. Nur mit dem Recht „Telefonie verwalten“. Die Messwerte kommen aus der
// Telefon-Engine im Browser (MOS, Jitter, Laufzeit, Paketverlust); die Bewertung steht unten auf der Seite.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { gespraecheApi, type QualitaetAntwort, type QualitaetZeile } from "../api/gespraeche";
import { Laden } from "../components/Bausteine";
import { FehlerBox } from "../components/Fehler";
import { Icon } from "../components/Icon";
import { Auswahl } from "../components/Auswahl";
import { useMeta } from "../daten/meta";
import { useSeitenKopf } from "../daten/seite";
import { heuteTag, istZeitraum, zeitraumGrenzen, zeitraumText, type Zeitraum } from "../aktivitaeten/zeiten";
import { dauerKurz } from "../aktivitaeten/zeiten";
import { KompaktWert } from "../kontakte/liste/KompaktWert";
import { T } from "../texte";
import { datumText, relativ, zeitpunktText } from "../werkzeuge/zeit";
import { GespraecheKopf, useDarfQualitaet } from "./GespraecheKopf";
import { BEWERTUNG_TEXT, detailPfad, geraetetestText, msText, prozentText, STOERUNG_ART_TEXT, zahlText } from "./logik";
import "./gespraeche.css";

const ZEITEN: { id: Zeitraum; label: string }[] = [
  { id: "heute", label: "Heute" },
  { id: "7tage", label: "Letzte 7 Tage" },
  { id: "30tage", label: "Letzte 30 Tage" },
  { id: "monat", label: "Dieser Monat" },
  { id: "eigen", label: "Zeitraum wählen …" },
];
const AUTO_AKTUALISIEREN_MS = 60_000;

function Bewertung({ b }: { b: QualitaetZeile["bewertung"] }) {
  return b ? <span className={`gesp-bewertung gesp-bewertung--${b}`}>{BEWERTUNG_TEXT[b]}</span> : <span className="leise">—</span>;
}

function Kachel({ label, wert, unter }: { label: string; wert: string; unter?: string }) {
  return (
    <div className="gesp-kachel">
      <span className="gesp-kachel__label">{label}</span>
      <span className="gesp-kachel__wert">{wert}</span>
      {unter ? <span className="gesp-kachel__unter">{unter}</span> : null}
    </div>
  );
}

function Stapel({ gut, mittel, schlecht }: { gut: number; mittel: number; schlecht: number }) {
  const summe = gut + mittel + schlecht;
  if (!summe) return <span className="leise">—</span>;
  const p = (n: number) => `${(100 * n) / summe}%`;
  return (
    <span className="gesp-stapel" title={`${gut} gut, ${mittel} mittel, ${schlecht} schlecht`}>
      <span className="gesp-stapel__gut" style={{ width: p(gut) }} />
      <span className="gesp-stapel__mittel" style={{ width: p(mittel) }} />
      <span className="gesp-stapel__schlecht" style={{ width: p(schlecht) }} />
    </span>
  );
}

function TageDiagramm({ tage }: { tage: NonNullable<QualitaetAntwort["verlauf"]> }) {
  const max = Math.max(1, ...tage.map((t) => t.anrufe));
  return (
    <div className="gesp-tage" role="img" aria-label="Gemessene Anrufe je Tag, schlechte rot">
      {tage.map((t) => {
        const hoehe = Math.max(3, Math.round((t.anrufe / max) * 70));
        const schlecht = t.anrufe ? Math.round((t.schlecht / t.anrufe) * hoehe) : 0;
        return (
          <div className="gesp-tag-saeule" key={t.tag} title={`${datumText(t.tag)}: ${t.anrufe} gemessene Anrufe, ${t.schlecht} schlecht${t.mos_durchschnitt !== null ? `, Ø MOS ${zahlText(t.mos_durchschnitt, 1)}` : ""}`}>
            <span className="gesp-tag-saeule__mos">{t.anrufe}</span>
            <span className="gesp-tag-saeule__balken" style={{ height: hoehe }}>
              {schlecht ? <span className="gesp-tag-saeule__schlecht" style={{ height: schlecht }} /> : null}
            </span>
            <span>{t.tag.slice(8, 10)}.{t.tag.slice(5, 7)}.</span>
          </div>
        );
      })}
    </div>
  );
}

export function Qualitaet() {
  useSeitenKopf([{ label: T.nav.gespraeche, to: "/gespraeche" }, { label: "Qualität" }]);
  const darf = useDarfQualitaet();
  const meta = useMeta();
  const [params, setParams] = useSearchParams();
  const zeitParam = params.get("zeit");
  const zeitraum: Zeitraum = istZeitraum(zeitParam) && ZEITEN.some((z) => z.id === zeitParam) ? zeitParam : "7tage";
  const user = params.get("user") || null;
  const eigenVon = params.get("von");
  const eigenBis = params.get("bis");
  const [daten, setDaten] = useState<QualitaetAntwort | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [laedt, setLaedt] = useState(true);
  const [nurAuffaellig, setNurAuffaellig] = useState(false);
  const [nochmal, setNochmal] = useState(0);

  const setParam = useCallback(
    (a: Record<string, string | null>) =>
      setParams(
        (alt) => {
          const n = new URLSearchParams(alt);
          for (const [k, v] of Object.entries(a)) (v ? n.set(k, v) : n.delete(k));
          return n;
        },
        { replace: true },
      ),
    [setParams],
  );

  const grenzen = useMemo(() => zeitraumGrenzen(zeitraum, heuteTag(), eigenVon, eigenBis), [zeitraum, eigenVon, eigenBis]);
  const schluessel = JSON.stringify([grenzen, user]);

  useEffect(() => {
    if (!darf) return;
    const ctrl = new AbortController();
    setLaedt(true);
    gespraecheApi
      .qualitaet({ von: grenzen.von, bis: grenzen.bis, user_id: user ?? undefined }, ctrl.signal)
      .then((r) => (setDaten(r), setFehler(null)))
      .catch((e) => (e as Error).name !== "AbortError" && setFehler(e))
      .finally(() => !ctrl.signal.aborted && setLaedt(false));
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schluessel, nochmal, darf]);

  // Stille Aktualisierung, solange die Seite sichtbar ist
  useEffect(() => {
    if (!darf) return;
    const t = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      gespraecheApi
        .qualitaet({ von: grenzen.von, bis: grenzen.bis, user_id: user ?? undefined })
        .then((r) => setDaten(r))
        .catch(() => undefined);
    }, AUTO_AKTUALISIEREN_MS);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schluessel, darf]);

  if (!darf) return <Navigate to="/gespraeche" replace />;

  const z = daten?.zusammenfassung ?? null;
  const zeilen = (daten?.data ?? []).filter((r) => !nurAuffaellig || r.bewertung === "mittel" || r.bewertung === "schlecht");
  const leer = !!daten && !laedt && !z?.gesamt && !daten.stoerungen?.length;

  return (
    <div className="gesp">
      <GespraecheKopf
        rechts={
          <button type="button" className="knopf knopf--klein gesp-filter__neu" onClick={() => setNochmal((n) => n + 1)} disabled={laedt}>
            <Icon name="neu-laden" groesse={13} />
            Aktualisieren
          </button>
        }
      />
      <div className="gesp-qual">
        <div className="gesp-qual__leiste">
          <span style={{ width: 170 }}>
            <Auswahl
              optionen={ZEITEN.map((x) => ({ wert: x.id, label: x.label }))}
              wert={zeitraum}
              breite={200}
              titel="Zeitraum"
              knopfInhalt={zeitraum === "eigen" ? <KompaktWert text={zeitraumText("eigen", eigenVon, eigenBis)} /> : undefined}
              onWahl={(w) => setParam({ zeit: w && w !== "7tage" ? String(w) : null, ...(w !== "eigen" ? { von: null, bis: null } : {}) })}
            />
          </span>
          {zeitraum === "eigen" ? (
            <span className="gesp-daten">
              <input type="date" className="gesp-datum" aria-label="Von (Datum)" value={eigenVon ?? ""} max={eigenBis ?? undefined} onChange={(e) => setParam({ von: e.target.value || null })} />
              <span className="leise">bis</span>
              <input type="date" className="gesp-datum" aria-label="Bis (Datum, einschließlich)" value={eigenBis ?? ""} min={eigenVon ?? undefined} onChange={(e) => setParam({ bis: e.target.value || null })} />
            </span>
          ) : null}
          <span style={{ width: 170 }}>
            <Auswahl
              optionen={meta.nutzerListe.map((n) => ({ wert: n.close_user_id ?? n.id, label: n.name, unter: n.is_active ? null : "inaktiv" }))}
              wert={user}
              leerErlaubt
              platzhalter="Alle Mitarbeiter"
              breite={240}
              titel="Nach Mitarbeiter filtern"
              onWahl={(w) => setParam({ user: (w as string | null) || null })}
            />
          </span>
        </div>

        {fehler ? <FehlerBox fehler={fehler} onNochmal={() => setNochmal((n) => n + 1)} /> : null}
        {laedt && !daten ? <Laden /> : null}

        {daten ? (
          <>
            {daten.alarme.length ? (
              <ul className="gesp-alarme" aria-label="Alarme der letzten 24 Stunden">
                <li className="gesp-alarme__titel">
                  <Icon name="warnung" groesse={14} /> Alarme der letzten 24 Stunden
                </li>
                {daten.alarme.map((a, i) => (
                  <li key={`${a.call_id}-${i}`}>
                    <span>{a.text}</span>
                    <Link to={detailPfad(a.call_id)}>Ansehen</Link>
                    <span className="gesp-hinweis">{relativ(a.zeit)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="gesp-ruhig">
                <Icon name="haken" groesse={14} /> Keine Alarme in den letzten 24 Stunden.
              </div>
            )}

            {leer ? (
              <p className="gesp-erklaerung">
                Im gewählten Zeitraum gibt es keine Anrufe der CRM-Telefonie. Anrufe aus Close haben keine Messwerte.
              </p>
            ) : null}

            {z ? (
              <div className="gesp-kacheln">
                <Kachel label="Gemessene Anrufe" wert={z.anrufe.toLocaleString("de-DE")} unter={z.gesamt ? `von ${z.gesamt.toLocaleString("de-DE")} Anrufen${z.ohne_messung ? `, ${z.ohne_messung} ohne Messung` : ""}` : undefined} />
                <Kachel label="Ø Gesprächsqualität (MOS)" wert={z.mos_durchschnitt === null ? "—" : zahlText(z.mos_durchschnitt, 1)} unter="4,0 und mehr ist sehr gut" />
                <Kachel label="Schlechte Anrufe" wert={z.schlecht.toLocaleString("de-DE")} unter={z.anrufe ? `${zahlText(z.anteil_schlecht_prozent ?? 0, 1)} % der gemessenen` : undefined} />
                <Kachel label="Ø Paketverlust" wert={z.paketverlust_durchschnitt === null || z.paketverlust_durchschnitt === undefined ? "—" : prozentText(z.paketverlust_durchschnitt)} unter={z.rtt_durchschnitt ? `Ø Laufzeit ${msText(z.rtt_durchschnitt)}` : undefined} />
                <Kachel label="Fehlgeschlagen" wert={(z.fehlgeschlagen ?? 0).toLocaleString("de-DE")} unter="Anrufe, die nicht zustande kamen" />
              </div>
            ) : null}

            {daten.verlauf?.length ? (
              <section>
                <h2 className="gesp-abschnitt__titel">Verlauf <span className="gesp-abschnitt__unter">gemessene Anrufe je Tag, schlechte in Rot</span></h2>
                <TageDiagramm tage={daten.verlauf} />
              </section>
            ) : null}

            {z?.je_nutzer.length ? (
              <section>
                <h2 className="gesp-abschnitt__titel">Je Mitarbeiter</h2>
                <div className="gesp-tabelle-rahmen">
                  <table className="gesp-tabelle">
                    <thead>
                      <tr>
                        <th>Mitarbeiter</th>
                        <th className="zahl">Anrufe</th>
                        <th className="zahl">Ø MOS</th>
                        <th>Verteilung</th>
                        <th className="zahl">Schlecht</th>
                        <th className="zahl">Ø Paketverlust</th>
                        <th className="zahl">Neu verbunden</th>
                      </tr>
                    </thead>
                    <tbody>
                      {z.je_nutzer.map((u) => (
                        <tr key={u.user_id}>
                          <td>{u.name ?? "Unbekannt"}</td>
                          <td className="zahl">{u.anrufe}</td>
                          <td className="zahl">{u.mos_durchschnitt === null ? "—" : zahlText(u.mos_durchschnitt, 1)}</td>
                          <td>
                            <Stapel gut={u.gut ?? 0} mittel={u.mittel ?? 0} schlecht={u.schlecht ?? 0} />
                          </td>
                          <td className="zahl">{u.schlecht ?? 0}</td>
                          <td className="zahl">{u.paketverlust_durchschnitt === null || u.paketverlust_durchschnitt === undefined ? "—" : prozentText(u.paketverlust_durchschnitt)}</td>
                          <td className="zahl">{u.wiederverbindungen ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}

            {daten.data.length ? (
              <section>
                <h2 className="gesp-abschnitt__titel">
                  Anrufe
                  <span className="gesp-abschnitt__unter">{zeilen.length.toLocaleString("de-DE")} von {daten.data.length.toLocaleString("de-DE")}</span>
                  <span className="segment" role="group" aria-label="Anzeige" style={{ marginLeft: "auto" }}>
                    <button type="button" className={`segment__knopf${!nurAuffaellig ? " segment__knopf--an" : ""}`} aria-pressed={!nurAuffaellig} onClick={() => setNurAuffaellig(false)}>
                      Alle
                    </button>
                    <button type="button" className={`segment__knopf${nurAuffaellig ? " segment__knopf--an" : ""}`} aria-pressed={nurAuffaellig} onClick={() => setNurAuffaellig(true)}>
                      Nur auffällige
                    </button>
                  </span>
                </h2>
                <div className="gesp-tabelle-rahmen">
                  <table className="gesp-tabelle">
                    <thead>
                      <tr>
                        <th>Zeit</th>
                        <th>Mitarbeiter</th>
                        <th className="zahl">Dauer</th>
                        <th className="zahl">MOS</th>
                        <th className="zahl">Jitter</th>
                        <th className="zahl">Laufzeit</th>
                        <th className="zahl">Paketverlust</th>
                        <th>Bewertung</th>
                        <th>Hinweise</th>
                      </tr>
                    </thead>
                    <tbody>
                      {zeilen.slice(0, 300).map((r) => (
                        <tr key={r.call_id}>
                          <td>
                            <Link to={detailPfad(r.call_id)} title="Details ansehen">
                              {zeitpunktText(r.zeit, false)}
                            </Link>
                          </td>
                          <td>{r.user_name ?? "Unbekannt"}</td>
                          <td className="zahl">{r.dauer_s ? dauerKurz(r.dauer_s) : ""}</td>
                          <td className="zahl">{zahlText(r.mos, 1)}</td>
                          <td className="zahl">{msText(r.jitter_ms)}</td>
                          <td className="zahl">{msText(r.rtt_ms)}</td>
                          <td className="zahl">{prozentText(r.paketverlust_prozent)}</td>
                          <td>
                            <Bewertung b={r.bewertung} />
                          </td>
                          <td className="text" title={r.warnungen.join(", ")}>
                            {r.warnungen.join(", ")}
                          </td>
                        </tr>
                      ))}
                      {!zeilen.length ? (
                        <tr>
                          <td colSpan={9} className="leise">
                            Keine auffälligen Anrufe im Zeitraum.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
                {zeilen.length > 300 ? <p className="gesp-erklaerung">Gezeigt sind die neuesten 300 Anrufe.</p> : null}
              </section>
            ) : null}

            {daten.stoerungen?.length ? (
              <section>
                <h2 className="gesp-abschnitt__titel">Störungsprotokoll <span className="gesp-abschnitt__unter">was bei Anrufen vorgefallen ist, neueste zuerst</span></h2>
                <div className="gesp-tabelle-rahmen">
                  <table className="gesp-tabelle">
                    <thead>
                      <tr>
                        <th>Zeit</th>
                        <th>Mitarbeiter</th>
                        <th>Art</th>
                        <th>Was vorfiel</th>
                      </tr>
                    </thead>
                    <tbody>
                      {daten.stoerungen.slice(0, 100).map((s, i) => (
                        <tr key={`${s.call_id}-${s.art}-${i}`}>
                          <td>
                            <Link to={detailPfad(s.call_id)}>{zeitpunktText(s.zeit, false)}</Link>
                          </td>
                          <td>{s.user_name ?? "Unbekannt"}</td>
                          <td>
                            <span className={`gesp-marke ${s.schwere === "hoch" ? "gesp-marke--rot" : "gesp-marke--gelb"}`}>{STOERUNG_ART_TEXT[s.art] ?? s.art}</span>
                          </td>
                          <td className="text" title={s.text}>
                            {s.text}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}

            {daten.geraetetests?.length ? (
              <section>
                <h2 className="gesp-abschnitt__titel">Gerätetests <span className="gesp-abschnitt__unter">letzter Test je Mitarbeiter, letzte 30 Tage</span></h2>
                <div className="gesp-tabelle-rahmen">
                  <table className="gesp-tabelle">
                    <thead>
                      <tr>
                        <th>Mitarbeiter</th>
                        <th>Zeit</th>
                        <th>Ergebnis</th>
                        <th className="zahl">MOS</th>
                        <th className="zahl">Laufzeit</th>
                        <th className="zahl">Jitter</th>
                        <th>Standort</th>
                      </tr>
                    </thead>
                    <tbody>
                      {daten.geraetetests.map((g) => (
                        <tr key={g.user_id}>
                          <td>{g.user_name ?? "Unbekannt"}</td>
                          <td>{zeitpunktText(g.zeit, false)}</td>
                          <td>
                            <span className={`gesp-bewertung gesp-bewertung--${g.ok ? "gut" : "schlecht"}`}>{geraetetestText(g.bewertung, g.ok)}</span>
                          </td>
                          <td className="zahl">{zahlText(g.mos, 1)}</td>
                          <td className="zahl">{msText(g.rtt_ms)}</td>
                          <td className="zahl">{msText(g.jitter_ms)}</td>
                          <td>{g.edge ?? ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}

            <p className="gesp-erklaerung">
              Bewertung: <strong>Gut</strong> ab MOS 3,7, <strong>mittel</strong> ab 3,1, darunter <strong>schlecht</strong>. Dazu zählen Paketverlust (ab 2 % mittel, ab 5 % schlecht),
              neu aufgebaute Verbindungen (ab einer mittel, ab drei schlecht) und kritische Warnungen wie ein stummes Mikrofon. Die Werte misst die Telefon-Engine im Browser
              während des Gesprächs; Anrufe aus Close und Anrufe vom Handy haben keine Messwerte.
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}
